// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState, type RefObject } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import { downscaleImage } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    files: File[];
    existingUrls?: string[];
    onChange: (files: File[]) => void;
    onRemoveExisting?: (url: string) => void;
    inputRef?: RefObject<HTMLInputElement | null>;
    accept?: string;
    maxFiles?: number;
    /**
     * Mounts a second, camera-only picker alongside the gallery one. Off by default — a screen that
     * uploads documents or logos has nothing to photograph.
     */
    withCamera?: boolean;
    /** Ref onto the camera picker, so the surrounding screen can render its own trigger for it. */
    cameraInputRef?: RefObject<HTMLInputElement | null>;
    /**
     * Fires while picked pictures are still being shrunk. The surrounding form must block its submit
     * on this — saving mid-shrink would send the request without the photo the user just took.
     */
    onPreparingChange?: (isPreparing: boolean) => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function FilesInput({
    files,
    existingUrls = [],
    onChange,
    onRemoveExisting,
    inputRef,
    accept = "image/*",
    maxFiles = 10,
    withCamera = false,
    cameraInputRef,
    onPreparingChange,
}: Props) {
    const t = useTranslation();

    const [isPreparing, setIsPreparing] = useState(false);

    /**
     * Shrinking is asynchronous, and a camera shortcut invites a second shot before the first is
     * done. Props are a render behind by then, so the list is kept here and claimed the moment it is
     * built: read from props alone, the second batch would overwrite the first instead of joining it.
     */
    const filesRef = useRef(files);
    const queueRef = useRef<Promise<void>>(Promise.resolve());
    const pendingRef = useRef(0);

    // Resynced from props for the times the list is replaced from outside — the parent clearing it
    // after a save, or the product detail arriving and resetting the form
    useEffect(() => {
        filesRef.current = files;
    }, [files]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files ?? []);
        // Cleared before the first await, or picking the same file twice in a row fires no event
        e.target.value = "";
        if (selected.length === 0) return;

        pendingRef.current += 1;
        setIsPreparing(true);
        onPreparingChange?.(true);

        queueRef.current = queueRef.current
            .then(async () => {
                const remaining = maxFiles - filesRef.current.length - existingUrls.length;
                if (remaining <= 0) return;
                const prepared = await Promise.all(selected.slice(0, remaining).map(downscaleImage));
                filesRef.current = [...filesRef.current, ...prepared];
                onChange(filesRef.current);
            })
            .finally(() => {
                pendingRef.current -= 1;
                if (pendingRef.current > 0) return;
                setIsPreparing(false);
                onPreparingChange?.(false);
            });
    };

    const handleRemove = (index: number) => {
        filesRef.current = files.filter((_, i) => i !== index);
        onChange(filesRef.current);
    };

    const hasAny = existingUrls.length > 0 || files.length > 0;

    return (
        <div className="files-input">
            <input ref={inputRef} type="file" accept={accept} multiple onChange={handleChange} className="files-input-hidden" />
            {/* `capture` is what makes the device open its camera instead of the gallery — the OS
                owns the permission prompt from there. */}
            {withCamera && (
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleChange}
                    className="files-input-hidden"
                />
            )}
            {isPreparing && (
                <p className="files-input-preparing">
                    <span className="files-input-spinner" />
                    {t.filesInput.preparing}
                </p>
            )}
            {hasAny && (
                <div className="files-input-grid">
                    {existingUrls.map((url) => (
                        <div key={url} className="files-input-preview">
                            <img src={url} alt="" className="files-input-image" />
                            {onRemoveExisting && (
                                <button type="button" className="files-input-remove" onClick={() => onRemoveExisting(url)}>
                                    <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                                </button>
                            )}
                        </div>
                    ))}
                    {files.map((file, index) => (
                        <div key={index} className="files-input-preview">
                            <img src={URL.createObjectURL(file)} alt="" className="files-input-image" />
                            <button type="button" className="files-input-remove" onClick={() => handleRemove(index)}>
                                <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
